import isEmpty from "lodash/isEmpty";
import React, {
  Fragment,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import Alert from "veneer/Alert";
import Button from "veneer/Button";
import Stack from "veneer/Stack";

import { sortByKeyAsc } from "cortex/ui/src/util/sortByProp";

import { CustomParamFormField } from "./CustomParameterFormField";
import Overrides from "./Overrides";
import ParamUtils, { PRIORITY, ParamValue, Priority } from "./utils";

interface CustomParam {
  errors?: string[];
  hasOverride: boolean;
  isDuplicate: boolean;
  isLegacy: boolean;
  isOverriding: boolean;
  k: string;
  v: unknown;
}

interface CustomParameterFormProps {
  deviceParams?: ParamValue[];
  legacyParams?: Set<string>;
  network: {
    id: number;
  };
  networkParams?: ParamValue[];
  readOnly: boolean;
  priority: Priority;
}

interface CustomParameterFormHandle {
  getParams: () => CustomParam[];
}

const defaultCustomParam = Object.freeze({
  k: "",
  v: "",
  errors: [],
  hasOverride: false,
  isOverriding: false,
  isDuplicate: false,
  isLegacy: false,
});

function excludeUnsetParams(param: CustomParam) {
  const { hasOverride, isOverriding } = param;
  return !(hasOverride && !isOverriding);
}

function excludeInvalidParams(param: CustomParam) {
  const { k, isDuplicate, isLegacy } = param;
  return !isDuplicate && !isLegacy && Boolean(k);
}

function getInitialParams({
  deviceParams = [],
  legacyParams = new Set<string>(),
  networkParams = [],
  priority,
}: CustomParameterFormProps) {
  const allParams = [...deviceParams, ...networkParams].filter(
    (param) => !legacyParams.has(param.name),
  );

  const initialParams = allParams.reduce<Record<string, CustomParam>>(
    (acc, { name }) => {
      const deviceParam = ParamUtils.getDeviceLevelParam(name, deviceParams);
      const networkParam = ParamUtils.getNetworkLevelParam(name, networkParams);
      const priorities = ParamUtils.buildPriorities(
        null,
        networkParam,
        deviceParam,
      );
      const isOverriding = Overrides.isOverriding(priority, priorities);
      const hasOverride = Overrides.hasOverride(priority, priorities);

      acc[name] = {
        ...defaultCustomParam,
        k: name,
        v: ParamUtils.getPriorityValue(name, { deviceParams, networkParams }),
        isLegacy: legacyParams.has(name),
        hasOverride,
        isOverriding,
      };
      acc[name].errors = ParamUtils.getErrorMsg(acc[name]);

      return acc;
    },
    {},
  );

  return { params: Object.values(initialParams).sort(sortByKeyAsc) };
}

const CustomParameterForm = forwardRef<
  CustomParameterFormHandle,
  CustomParameterFormProps
>(
  (
    {
      deviceParams = [],
      legacyParams = new Set(),
      network,
      networkParams = [],
      priority,
      readOnly,
    },
    ref,
  ) => {
    const initialValues = useMemo(
      () =>
        getInitialParams({
          deviceParams,
          legacyParams,
          network,
          networkParams,
          priority,
          readOnly,
        }),
      [deviceParams, legacyParams, network, networkParams, priority, readOnly],
    );
    const [params, setParams] = useState<CustomParam[]>(
      () => initialValues.params,
    );

    const resetFieldValue = useCallback(
      (idx: number, isOverriding: boolean, resetPriority = PRIORITY.MAX) => {
        setParams((currentParams) =>
          currentParams.map((param, paramIdx) => {
            if (paramIdx !== idx) {
              return param;
            }

            return {
              ...param,
              v: ParamUtils.getPriorityValue(param.k, {
                deviceParams,
                networkParams,
                priority: resetPriority,
              }),
              isOverriding,
            };
          }),
        );
      },
      [deviceParams, networkParams],
    );

    const onOverride = useCallback(
      (idx: number, isOverriding: boolean) => {
        const resetPriority = isOverriding ? PRIORITY.MAX : priority;
        resetFieldValue(idx, isOverriding, resetPriority);
      },
      [priority, resetFieldValue],
    );

    useImperativeHandle(
      ref,
      () => ({
        getParams: () =>
          params.filter(excludeUnsetParams).filter(excludeInvalidParams),
      }),
      [params],
    );

    const addParam = (): void => {
      setParams((currentParams) => [
        ...currentParams,
        { ...defaultCustomParam },
      ]);
    };

    const removeParam = (idx: number): void => {
      setParams((currentParams) =>
        currentParams.filter((_param, paramIdx) => paramIdx !== idx),
      );
    };

    const updateParam = (idx: number, value: CustomParam): void => {
      setParams((currentParams) =>
        currentParams.map((param, paramIdx) =>
          paramIdx === idx ? value : param,
        ),
      );
    };

    const renderAddParamButton = (hasParameter: boolean, onAdd: () => void) => (
      <Button
        disabled={readOnly}
        icon="add"
        onClick={onAdd}
        variant={hasParameter ? "contained" : "outlined"}
      >
        Add Parameter
      </Button>
    );

    const renderCustomParams = () => {
      const hasParameter = !isEmpty(params);

      return (
        <>
          {hasParameter ? (
            params.map((param, idx) => (
              <Fragment key={`param-${idx}`}>
                <CustomParamFormField
                  index={idx}
                  legacyParams={legacyParams}
                  network={network}
                  networkParams={networkParams}
                  onChange={(value) => updateParam(idx, value)}
                  onOverride={onOverride}
                  onRemove={() => removeParam(idx)}
                  params={params}
                  priority={priority}
                  readOnly={readOnly}
                  value={param}
                />
              </Fragment>
            ))
          ) : (
            <Alert dense severity="info">
              No Custom Parameters are set.
            </Alert>
          )}
          <Stack justifyContent="flex-end" mt={2}>
            {renderAddParamButton(hasParameter, addParam)}
          </Stack>
        </>
      );
    };

    return (
      <form onSubmit={(event) => event.preventDefault()}>
        {renderCustomParams()}
      </form>
    );
  },
);

export {
  CustomParameterForm,
  defaultCustomParam,
  excludeInvalidParams,
  excludeUnsetParams,
  getInitialParams,
};
export type { CustomParameterFormHandle };
